import { useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import {
  useListAuditLogs,
  useListStatuses,
  useListCategories,
  ListAuditLogsActionType,
  AuditLog,
} from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { ClipboardList, X, ChevronDown, ChevronUp } from "lucide-react";

const ACTION_LABELS: Record<string, string> = {
  "report.create": "Report Created",
  "report.update": "Report Updated",
  "report.delete": "Report Deleted",
  "comment.create": "Comment Added",
  "comment.delete": "Comment Deleted",
  "issue.create": "Issue Created",
  "issue.update": "Issue Updated",
  "issue.delete": "Issue Deleted",
};

const ACTION_VARIANTS: Record<
  string,
  { bg: string; text: string }
> = {
  "report.create": { bg: "bg-blue-100 dark:bg-blue-900/40", text: "text-blue-700 dark:text-blue-300" },
  "report.update": { bg: "bg-amber-100 dark:bg-amber-900/40", text: "text-amber-700 dark:text-amber-300" },
  "report.delete": { bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-300" },
  "comment.create": { bg: "bg-emerald-100 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-300" },
  "comment.delete": { bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-300" },
  "issue.create": { bg: "bg-purple-100 dark:bg-purple-900/40", text: "text-purple-700 dark:text-purple-300" },
  "issue.update": { bg: "bg-amber-100 dark:bg-amber-900/40", text: "text-amber-700 dark:text-amber-300" },
  "issue.delete": { bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-300" },
};

function ActionBadge({ actionType }: { actionType: string }) {
  const v = ACTION_VARIANTS[actionType] ?? { bg: "bg-slate-100", text: "text-slate-700" };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${v.bg} ${v.text}`}
    >
      {ACTION_LABELS[actionType] ?? actionType}
    </span>
  );
}

type ResolveDiff = (field: string, value: unknown) => string;

function DiffDisplay({
  diff,
  resolve,
}: {
  diff: Record<string, { before: unknown; after: unknown }>;
  resolve: ResolveDiff;
}) {
  return (
    <div className="mt-2 space-y-1">
      {Object.entries(diff).map(([field, change]) => (
        <div key={field} className="text-xs font-mono flex gap-2 items-center">
          <span className="text-slate-500 capitalize">{field}:</span>
          <span className="text-red-600 dark:text-red-400 line-through">
            {resolve(field, change.before)}
          </span>
          <span className="text-slate-400">→</span>
          <span className="text-emerald-600 dark:text-emerald-400">
            {resolve(field, change.after)}
          </span>
        </div>
      ))}
    </div>
  );
}

function AuditLogRow({ log, resolveDiff }: { log: AuditLog; resolveDiff: ResolveDiff }) {
  const [expanded, setExpanded] = useState(false);
  const hasDiff =
    log.diff && typeof log.diff === "object" && Object.keys(log.diff).length > 0;

  return (
    <TableRow className="align-top">
      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
        {format(new Date(log.createdAt), "MMM d, yyyy")}
        <div className="text-[10px] text-slate-400">
          {format(new Date(log.createdAt), "p")}
        </div>
      </TableCell>
      <TableCell>
        <ActionBadge actionType={log.actionType} />
      </TableCell>
      <TableCell>
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {log.description}
        </span>
        {hasDiff && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="ml-2 inline-flex items-center gap-0.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            {expanded ? (
              <><ChevronUp className="w-3 h-3" /> hide diff</>
            ) : (
              <><ChevronDown className="w-3 h-3" /> show diff</>
            )}
          </button>
        )}
        {expanded && hasDiff && (
          <DiffDisplay
            diff={log.diff as Record<string, { before: unknown; after: unknown }>}
            resolve={resolveDiff}
          />
        )}
      </TableCell>
      <TableCell className="text-xs">
        {log.actorName ? (
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {log.actorName}
          </span>
        ) : (
          <span className="text-slate-400 italic">Carrier</span>
        )}
      </TableCell>
      <TableCell className="text-xs text-slate-500 font-mono">
        #{log.targetId}
      </TableCell>
    </TableRow>
  );
}

export default function StaffAuditLogPage() {
  const [reportIdInput, setReportIdInput] = useState("");
  const [actorInput, setActorInput] = useState("");
  const [actionTypeFilter, setActionTypeFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const params = {
    ...(reportIdInput && !isNaN(Number(reportIdInput))
      ? { reportId: Number(reportIdInput) }
      : {}),
    ...(actionTypeFilter && actionTypeFilter !== "all"
      ? { actionType: actionTypeFilter as ListAuditLogsActionType }
      : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };

  const { data: logs, isLoading } = useListAuditLogs(params);
  const { data: statuses } = useListStatuses();
  const { data: categories } = useListCategories();

  // Translate raw FK ids into human-readable labels in diff payloads.
  const statusLabelById = new Map((statuses ?? []).map((s) => [s.id, s.label]));
  const categoryNameById = new Map((categories ?? []).map((c) => [c.id, c.name]));
  const resolveDiff: ResolveDiff = (field, value) => {
    if (value == null) return "(none)";
    if (field === "statusId") {
      const n = Number(value);
      return Number.isFinite(n) ? (statusLabelById.get(n) ?? String(value)) : String(value);
    }
    if (field === "categoryId") {
      const n = Number(value);
      return Number.isFinite(n) ? (categoryNameById.get(n) ?? String(value)) : String(value);
    }
    return String(value);
  };

  const filteredLogs = logs?.filter((log) => {
    if (actorInput && log.actorName) {
      return log.actorName.toLowerCase().includes(actorInput.toLowerCase());
    }
    if (actorInput && !log.actorName) return false;
    return true;
  });

  const hasActiveFilters =
    reportIdInput ||
    actorInput ||
    (actionTypeFilter && actionTypeFilter !== "all") ||
    startDate ||
    endDate;

  const clearFilters = () => {
    setReportIdInput("");
    setActorInput("");
    setActionTypeFilter("all");
    setStartDate("");
    setEndDate("");
  };

  return (
    <StaffLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-primary" />
            Audit Log
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            All staff mutations across reports, issues, and comments.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4 flex-wrap">
        <Input
          placeholder="Filter by report ID..."
          value={reportIdInput}
          onChange={(e) => setReportIdInput(e.target.value)}
          className="sm:w-40 font-mono"
          type="number"
          min={1}
        />
        <Input
          placeholder="Filter by actor name..."
          value={actorInput}
          onChange={(e) => setActorInput(e.target.value)}
          className="sm:w-52"
        />
        <Select value={actionTypeFilter} onValueChange={setActionTypeFilter}>
          <SelectTrigger className="sm:w-48">
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="report.create">Report Created</SelectItem>
            <SelectItem value="report.update">Report Updated</SelectItem>
            <SelectItem value="comment.create">Comment Added</SelectItem>
            <SelectItem value="issue.create">Issue Created</SelectItem>
            <SelectItem value="issue.update">Issue Updated</SelectItem>
            <SelectItem value="issue.delete">Issue Deleted</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="sm:w-44"
          placeholder="Start date"
          title="Start date"
        />
        <Input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="sm:w-44"
          placeholder="End date"
          title="End date"
        />
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="icon"
            onClick={clearFilters}
            className="shrink-0"
            title="Clear filters"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      <Card className="border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
                <TableRow>
                  <TableHead className="w-32">Timestamp</TableHead>
                  <TableHead className="w-40">Action</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-36">Actor</TableHead>
                  <TableHead className="w-20">Target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!filteredLogs || filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-12 text-slate-500"
                    >
                      <ClipboardList className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      {hasActiveFilters
                        ? "No entries match your filters."
                        : "No audit log entries yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <AuditLogRow key={log.id} log={log} resolveDiff={resolveDiff} />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {filteredLogs && filteredLogs.length > 0 && (
          <div className="px-4 py-2 border-t bg-slate-50 dark:bg-slate-900/50 text-xs text-slate-500">
            Showing {filteredLogs.length} entr{filteredLogs.length === 1 ? "y" : "ies"}
            {filteredLogs.length === 500 && " (limit reached — apply filters to narrow results)"}
          </div>
        )}
      </Card>
    </StaffLayout>
  );
}
